import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BUSINESS_VALUE_SHORT_LABELS,
  filterBusinessValueRows,
} from '@/lib/businessValueCategories';

const COLORS = ['#2f6fed', '#10b981', '#f59e0b', '#8b5cf6'];

function formatCostLabel(v) {
  const n = Number(v || 0);
  return n < 1 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

export default function TokenUsageCharts({ data }) {
  if (!data) return null;

  const byType = filterBusinessValueRows(data.by_ticket_type || []).map((row) => ({
    key: row.ticket_type_key,
    name: row.display_name,
    shortName: BUSINESS_VALUE_SHORT_LABELS[row.ticket_type_key] || row.display_name,
    cost: row.llm_cost,
    tokens: row.total_tokens,
    deploys: row.deploy_count || 0,
  }));

  const byPeriod = (data.by_period || []).map((row) => ({
    name: row.period_label,
    cost: row.llm_cost,
    tokens: row.total_tokens,
  }));

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>LLM cost by ticket type</CardTitle>
          <CardDescription>Bedrock usage in the selected date range</CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          {byType.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No usage recorded yet. Run a ticket through the master agent.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={byType}
                layout="vertical"
                margin={{ top: 8, right: 56, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => (v < 1 ? v.toFixed(3) : v.toFixed(2))}
                />
                <YAxis
                  type="category"
                  dataKey="shortName"
                  width={72}
                  tick={{ fontSize: 12, fill: '#374151' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(v, _n, props) => {
                    const deploys = props?.payload?.deploys;
                    const extra = deploys ? ` · ${deploys} deploy${deploys === 1 ? '' : 's'}` : '';
                    return [`${formatCostLabel(v)}${extra}`, 'LLM cost'];
                  }}
                  labelFormatter={(_label, payload) => payload?.[0]?.payload?.name || _label}
                />
                <Bar dataKey="cost" fill={COLORS[0]} name="LLM cost" radius={[0, 4, 4, 0]} barSize={28}>
                  <LabelList
                    dataKey="cost"
                    position="right"
                    formatter={formatCostLabel}
                    style={{ fontSize: 11, fill: '#374151' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>LLM cost over time</CardTitle>
          <CardDescription>Daily or weekly buckets</CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          {byPeriod.length === 0 ? (
            <p className="text-sm text-muted-foreground">No trend data for this range.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={byPeriod}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => (v < 1 ? v.toFixed(3) : v.toFixed(2))} />
                <Tooltip formatter={(v) => [formatCostLabel(v), 'LLM cost']} />
                <Line type="monotone" dataKey="cost" stroke={COLORS[1]} name="LLM cost" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
